<template>
  <PublicLayout page-title="Política de privacidad">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <!-- Page header -->
      <header class="text-center mb-12">
        <h1 class="text-3xl md:text-4xl font-bold text-legal-900 mb-4 tracking-tight">
          {{ pageTitle }}
        </h1>
        <p class="text-lg text-legal-600 max-w-2xl mx-auto">
          {{ pageDescription }}
        </p>
        <div class="flex items-center justify-center space-x-4 mt-6 text-sm text-legal-500">
          <span class="flex items-center">
            <CalendarDaysIcon class="w-4 h-4 mr-1" aria-hidden="true" />
            Actualizada: {{ lastUpdated }}
          </span>
          <span class="flex items-center">
            <ScaleIcon class="w-4 h-4 mr-1" aria-hidden="true" />
            Versión: {{ policyVersion }}
          </span>
        </div>
      </header>

      <!-- Content sections -->
      <article class="space-y-12">

        <!-- Section 1: Responsable del Tratamiento -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6 tracking-wide">1. Responsable del tratamiento</h2>
          <div class="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-slate-200">
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <h3 class="font-medium text-legal-900 mb-2">Identificación</h3>
                <p class="text-legal-700"><strong>Responsable:</strong> {{ RESPONSABLE_TRATAMIENTO.nombre }}</p>
                <p class="text-legal-700"><strong>NIF:</strong> {{ RESPONSABLE_TRATAMIENTO.nif }}</p>
                <p class="text-legal-700"><strong>Domicilio:</strong> {{ RESPONSABLE_TRATAMIENTO.domicilio }}</p>
              </div>
              <div>
                <h3 class="font-medium text-legal-900 mb-2">Datos de contacto</h3>
                <p class="text-legal-700">
                  <strong>Email:</strong>
                  <a :href="`mailto:${RESPONSABLE_TRATAMIENTO.email}`" class="text-teal-600 hover:text-teal-700">
                    {{ RESPONSABLE_TRATAMIENTO.email }}
                  </a>
                </p>
              </div>
            </div>
          </div>
          <p class="text-legal-600 leading-relaxed">
            En cumplimiento del Reglamento (UE) 2016/679 General de Protección de Datos (RGPD) y la
            Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los
            derechos digitales (LOPDGDD), le informamos sobre el tratamiento de sus datos personales
            a través de la plataforma Olvidadme.
          </p>
        </section>

        <!-- Section 2: Ámbito de Aplicación -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">2. Ámbito de aplicación de la política</h2>
          <p class="text-legal-600 leading-relaxed mb-6">
            Esta política de privacidad se aplica al tratamiento de datos personales realizado a través
            de la plataforma Olvidadme para la prestación de los siguientes servicios:
          </p>

          <div class="grid gap-6 md:grid-cols-3">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-3 flex items-center">
                <ShieldCheckIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Derecho al Olvido
              </h3>
              <p class="text-legal-600 text-sm">
                Ejercicio del derecho de supresión de datos personales en internet para personas vivas,
                conforme al artículo 17 del RGPD.
              </p>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-3 flex items-center">
                <HeartIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Post-Mortem Digital
              </h3>
              <p class="text-legal-600 text-sm">
                Gestión del legado digital de personas fallecidas por parte de herederos o personas
                vinculadas, conforme al artículo 96 de la LOPDGDD.
              </p>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-3 flex items-center">
                <DocumentTextIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Testamento Digital
              </h3>
              <p class="text-legal-600 text-sm">
                Planificación del legado digital mediante cláusulas testamentarias, designación de
                albaceas digitales y herederos.
              </p>
            </div>
          </div>
        </section>

        <!-- Section 3: Categorías de Datos Personales -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">3. Categorías de datos personales tratados</h2>

          <div class="space-y-6">
            <!-- Datos de personas vivas -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <UserIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Datos de personas vivas (solicitantes)
              </h3>
              <ul class="space-y-2 text-legal-600">
                <li>Nombre y apellidos</li>
                <li>Documento Nacional de Identidad (DNI/NIE)</li>
                <li>Dirección postal</li>
                <li>Correo electrónico</li>
                <li>Teléfono de contacto (opcional)</li>
                <li>URLs objeto de solicitud de supresión (máximo 5)</li>
              </ul>
            </div>

            <!-- Datos de personas fallecidas -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <HeartIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Datos de personas fallecidas (servicio post-mortem)
              </h3>
              <ul class="space-y-2 text-legal-600">
                <li>Nombre y apellidos del fallecido</li>
                <li>DNI del fallecido</li>
                <li>Fecha de fallecimiento</li>
                <li>Relación del solicitante con el fallecido</li>
                <li>URLs objeto de gestión del legado digital</li>
              </ul>
            </div>

            <!-- Documentación -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <DocumentTextIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Documentación acreditativa
              </h3>
              <ul class="space-y-2 text-legal-600">
                <li>Copia del DNI del solicitante</li>
                <li>Certificado de defunción (servicio post-mortem)</li>
                <li>Documentación acreditativa del vínculo familiar o testamentario</li>
                <li>Poderes de representación, en su caso</li>
              </ul>
            </div>

            <!-- Datos de facturación -->
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <CreditCardIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Datos de facturación
              </h3>
              <ul class="space-y-2 text-legal-600">
                <li>Datos fiscales (NIF, dirección fiscal)</li>
                <li>Información de pagos procesados a través de Stripe</li>
                <li>Historial de servicios contratados</li>
              </ul>
            </div>

            <!-- Aviso importante -->
            <div class="bg-teal-50 border border-teal-200 rounded-2xl p-6">
              <h3 class="font-medium text-teal-900 mb-3 flex items-center">
                <LockClosedIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Datos que NO almacenamos
              </h3>
              <p class="text-teal-800 text-sm">
                <strong>Olvidadme no almacena, en ningún caso:</strong> contraseñas de cuentas de terceros,
                claves privadas, semillas de recuperación (seeds), ni credenciales de acceso a plataformas
                externas. Nuestro servicio se limita a la gestión documental y legal.
              </p>
            </div>
          </div>
        </section>

        <!-- Section 4: Finalidades del Tratamiento -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">4. Finalidades del tratamiento</h2>
          <p class="text-legal-600 leading-relaxed mb-6">
            Sus datos personales serán tratados para las siguientes finalidades:
          </p>

          <div class="space-y-4">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-2">Prestación del servicio contratado</h3>
              <p class="text-legal-600 text-sm">
                Gestión de solicitudes de derecho al olvido, servicios post-mortem o testamento digital,
                incluyendo la elaboración de documentación legal y comunicación con las plataformas destinatarias.
              </p>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-2">Gestión administrativa y contable</h3>
              <p class="text-legal-600 text-sm">
                Facturación, contabilidad y cumplimiento de obligaciones fiscales derivadas de la
                relación contractual.
              </p>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-2">Comunicaciones relacionadas con el servicio</h3>
              <p class="text-legal-600 text-sm">
                Envío de notificaciones sobre el estado de su solicitud, actualizaciones del caso y
                comunicaciones necesarias para la prestación del servicio.
              </p>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-2">Cumplimiento de obligaciones legales</h3>
              <p class="text-legal-600 text-sm">
                Atención a requerimientos de autoridades competentes y cumplimiento de la normativa
                aplicable en materia de protección de datos y comercio electrónico.
              </p>
            </div>
          </div>
        </section>

        <!-- Section 5: Base Jurídica del Tratamiento -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">5. Base jurídica del tratamiento</h2>

          <div class="overflow-x-auto">
            <table class="w-full border border-slate-200 rounded-2xl bg-white shadow-lg">
              <thead class="bg-teal-50">
                <tr>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Tratamiento</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Base jurídica</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Normativa</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-legal-200">
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Prestación del servicio contratado</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Ejecución de contrato</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Art. 6.1.b) RGPD</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Obligaciones fiscales y contables</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Obligación legal</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Art. 6.1.c) RGPD</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Gestión de reclamaciones y defensa jurídica</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Interés legítimo</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Art. 6.1.f) RGPD</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Servicio post-mortem digital</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Legitimación específica</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Art. 96 LOPDGDD</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-100">
            <h3 class="font-medium text-legal-900 mb-3">Sobre el servicio post-mortem</h3>
            <p class="text-legal-600 text-sm">
              El tratamiento de datos de personas fallecidas se realiza conforme al artículo 96 de la
              LOPDGDD, que reconoce el derecho de acceso a los contenidos gestionados por prestadores
              de servicios de la sociedad de la información sobre personas fallecidas a sus familiares
              o herederos, salvo que el fallecido lo hubiera prohibido expresamente.
            </p>
          </div>
        </section>

        <!-- Section 6: Destinatarios de los Datos -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">6. Destinatarios de los datos</h2>

          <div class="space-y-6">
            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <GlobeEuropeAfricaIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Comunicaciones necesarias para el servicio
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li><strong>Plataformas destinatarias:</strong> Los datos se comunicarán a las plataformas frente a las que se ejercite el derecho, en la medida necesaria para la prestación del servicio.</li>
                <li><strong>Administración tributaria:</strong> Cumplimiento de obligaciones fiscales.</li>
                <li><strong>Autoridades judiciales:</strong> En caso de requerimiento judicial.</li>
              </ul>
            </div>

            <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <ServerIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Proveedores de servicios (encargados del tratamiento)
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li><strong>MongoDB Atlas:</strong> Alojamiento de base de datos (Irlanda - UE)</li>
                <li><strong>Vercel:</strong> Hosting de aplicación web (Alemania - UE)</li>
                <li><strong>Resend:</strong> Servicio de envío de emails transaccionales</li>
                <li><strong>Cloudinary:</strong> Almacenamiento seguro de documentos (Irlanda - UE)</li>
                <li><strong>Stripe:</strong> Procesamiento de pagos (certificación PCI DSS)</li>
              </ul>
            </div>

            <div class="bg-teal-50 border border-teal-200 rounded-2xl p-6">
              <h3 class="font-medium text-teal-900 mb-3">Transferencias internacionales</h3>
              <p class="text-teal-800 text-sm">
                Los datos se tratan preferentemente en territorio de la Unión Europea. En caso de
                transferencias a terceros países, se garantiza un nivel de protección adecuado mediante
                decisiones de adecuación de la Comisión Europea o cláusulas contractuales tipo.
              </p>
            </div>
          </div>
        </section>

        <!-- Section 7: Plazos de Conservación -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">7. Plazos de conservación</h2>

          <div class="overflow-x-auto">
            <table class="w-full border border-slate-200 rounded-2xl bg-white shadow-lg">
              <thead class="bg-teal-50">
                <tr>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Datos</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Plazo de conservación</th>
                  <th class="px-6 py-3 text-left text-sm font-medium text-legal-900">Fundamento</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-legal-200">
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Datos del caso y documentación</td>
                  <td class="px-6 py-4 text-sm text-legal-600">5 años en bloqueo tras finalización</td>
                  <td class="px-6 py-4 text-sm text-legal-500">Prescripción de acciones legales</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Datos de facturación</td>
                  <td class="px-6 py-4 text-sm text-legal-600">6 años</td>
                  <td class="px-6 py-4 text-sm text-legal-500">Art. 30 Código de Comercio</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Consentimientos RGPD</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Mientras esté vigente + 5 años</td>
                  <td class="px-6 py-4 text-sm text-legal-500">Prueba del consentimiento</td>
                </tr>
                <tr>
                  <td class="px-6 py-4 text-sm text-legal-700">Datos de cuenta de usuario</td>
                  <td class="px-6 py-4 text-sm text-legal-600">Hasta solicitud de baja + plazos legales</td>
                  <td class="px-6 py-4 text-sm text-legal-500">Ejecución del contrato</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-6 bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <h3 class="font-medium text-legal-900 mb-3">Bloqueo de datos</h3>
            <p class="text-legal-600 text-sm">
              Transcurrido el plazo de conservación activa, los datos se mantendrán bloqueados durante
              los plazos de prescripción legalmente establecidos, únicamente a disposición de jueces,
              tribunales, Ministerio Fiscal o Administraciones Públicas competentes. Una vez transcurridos
              dichos plazos, los datos serán suprimidos de forma segura e irreversible.
            </p>
          </div>
        </section>

        <!-- Section 8: Derechos de las Personas Interesadas -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">8. Derechos de las personas interesadas</h2>
          <p class="text-legal-600 leading-relaxed mb-6">
            Puede ejercer los siguientes derechos reconocidos en el RGPD (derechos ARSULIPO):
          </p>

          <div class="grid gap-4 md:grid-cols-2">
            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <EyeIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Acceso
              </h3>
              <p class="text-legal-600 text-sm">
                Conocer qué datos personales estamos tratando.
              </p>
            </div>

            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <PencilIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Rectificación
              </h3>
              <p class="text-legal-600 text-sm">
                Corregir datos inexactos o completar datos incompletos.
              </p>
            </div>

            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <TrashIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Supresión
              </h3>
              <p class="text-legal-600 text-sm">
                Solicitar la eliminación de sus datos cuando ya no sean necesarios.
              </p>
            </div>

            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <PauseIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Limitación
              </h3>
              <p class="text-legal-600 text-sm">
                Suspender el tratamiento en determinadas circunstancias.
              </p>
            </div>

            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <ArrowPathIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Portabilidad
              </h3>
              <p class="text-legal-600 text-sm">
                Recibir sus datos en formato estructurado y transmitirlos.
              </p>
            </div>

            <div class="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-6 border border-teal-100">
              <h3 class="font-medium text-legal-900 mb-2 flex items-center">
                <XMarkIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Oposición
              </h3>
              <p class="text-legal-600 text-sm">
                Oponerse al tratamiento de sus datos en determinados casos.
              </p>
            </div>
          </div>

          <!-- Ejercicio de derechos -->
          <div class="mt-8 bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <h3 class="font-medium text-legal-900 mb-4 flex items-center">
              <ChatBubbleLeftEllipsisIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
              Cómo ejercer sus derechos
            </h3>
            <div class="space-y-4">
              <p class="text-legal-600 text-sm">
                Puede ejercer sus derechos enviando una comunicación a:
              </p>
              <div class="bg-teal-50 rounded-lg p-4">
                <p class="text-legal-700 text-sm"><strong>Email:</strong>
                  <a :href="`mailto:${RESPONSABLE_TRATAMIENTO.email}`" class="text-teal-600 hover:text-teal-700">
                    {{ RESPONSABLE_TRATAMIENTO.email }}
                  </a>
                </p>
                <p class="text-legal-700 text-sm"><strong>Dirección postal:</strong> {{ RESPONSABLE_TRATAMIENTO.domicilio }}</p>
              </div>
              <p class="text-legal-600 text-sm">
                Deberá acreditar su identidad adjuntando copia de su DNI o documento equivalente.
              </p>
            </div>
          </div>

          <!-- Reclamación AEPD -->
          <div class="mt-6 bg-red-50 border border-red-200 rounded-2xl p-6">
            <h3 class="font-medium text-red-900 mb-3 flex items-center">
              <ScaleIcon class="w-5 h-5 mr-2 text-red-600" aria-hidden="true" />
              Derecho a reclamar ante la AEPD
            </h3>
            <p class="text-red-800 text-sm">
              Si considera que el tratamiento de sus datos no es conforme a la normativa, puede presentar
              una reclamación ante la Agencia Española de Protección de Datos (AEPD):
              <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" class="text-red-700 hover:text-red-800 underline">
                www.aepd.es
              </a>
            </p>
          </div>
        </section>

        <!-- Section 9: Medidas de Seguridad -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">9. Medidas de seguridad</h2>

          <p class="text-legal-600 leading-relaxed mb-6">
            Olvidadme ha adoptado las medidas técnicas y organizativas necesarias para garantizar la
            seguridad de los datos personales y evitar su alteración, pérdida, tratamiento o acceso
            no autorizado:
          </p>

          <div class="grid gap-6 md:grid-cols-2">
            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <LockClosedIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Medidas técnicas
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>Cifrado de datos sensibles (AES-256-GCM)</li>
                <li>Comunicaciones cifradas (HTTPS/TLS)</li>
                <li>Control de acceso basado en roles</li>
                <li>Copias de seguridad automatizadas</li>
                <li>Monitorización de seguridad</li>
              </ul>
            </div>

            <div class="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg">
              <h3 class="font-medium text-legal-900 mb-4 flex items-center">
                <UserGroupIcon class="w-5 h-5 mr-2 text-teal-600" aria-hidden="true" />
                Medidas organizativas
              </h3>
              <ul class="space-y-2 text-legal-600 text-sm">
                <li>Políticas de acceso y confidencialidad</li>
                <li>Registro de actividades de tratamiento</li>
                <li>Procedimientos de gestión de incidentes</li>
                <li>Evaluación periódica de riesgos</li>
                <li>Formación en protección de datos</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Section 10: Actualización de la Política -->
        <section class="mb-12">
          <h2 class="text-3xl font-bold text-legal-900 mb-6">10. Actualización de la política</h2>

          <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
            <p class="text-legal-600 leading-relaxed mb-4">
              Esta política de privacidad puede ser actualizada periódicamente para adaptarla a cambios
              normativos o en nuestros servicios. Le recomendamos revisar esta página regularmente.
            </p>
            <p class="text-legal-600 leading-relaxed">
              Cualquier modificación sustancial será comunicada a los usuarios registrados a través del
              correo electrónico asociado a su cuenta.
            </p>
            <div class="mt-4 pt-4 border-t border-legal-200">
              <p class="text-legal-500 text-sm">
                <strong>Última actualización:</strong> {{ lastUpdated }}
              </p>
              <p class="text-legal-500 text-sm">
                <strong>Versión:</strong> {{ policyVersion }}
              </p>
            </div>
          </div>
        </section>

        <!-- Footer section -->
        <footer class="text-center pt-8 border-t border-legal-200">
          <p class="text-legal-500 text-sm">
            Para cualquier consulta relacionada con esta política de privacidad, puede contactar con nosotros en
            <a :href="`mailto:${RESPONSABLE_TRATAMIENTO.email}`" class="text-teal-600 hover:text-teal-700">
              {{ RESPONSABLE_TRATAMIENTO.email }}
            </a>
          </p>
        </footer>
      </article>
    </div>
  </PublicLayout>
</template>

<script setup>
import { ref } from 'vue'
import { useSeo } from '@/composables/useSeo'
import PublicLayout from '@/components/layout/PublicLayout.vue'
import { RESPONSABLE_TRATAMIENTO } from '@/utils/constants/legal'

// SEO Meta Tags
useSeo({
  title: 'Política de Privacidad',
  description: 'Cómo protegemos tus datos según RGPD. Tratamiento de datos personales en Olvidadme.',
  canonical: '/politica-privacidad'
})

// Heroicons imports
import {
  CalendarDaysIcon,
  ScaleIcon,
  LockClosedIcon,
  PencilIcon,
  TrashIcon,
  PauseIcon,
  UserIcon,
  ChatBubbleLeftEllipsisIcon,
  UserGroupIcon,
  GlobeEuropeAfricaIcon,
  EyeIcon,
  ArrowPathIcon,
  XMarkIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  HeartIcon,
  DocumentTextIcon,
  ServerIcon
} from '@heroicons/vue/24/outline'

/**
 * PrivacyPolicy Component for Olvidadme
 *
 * Implementing Legal Pages Pattern from PATTERNS_FROM_BASKET_BASE.md
 * GDPR-compliant privacy policy with professional legal structure
 *
 * Features:
 * - Comprehensive RGPD and LOPDGDD compliance
 * - 10 sections covering all legal requirements
 * - Three services: Derecho al Olvido, Post-Mortem, Testamento Digital
 * - Clear data processing explanations with legal bases
 * - User rights information (ARSULIPO) with AEPD contact
 * - Responsive design with professional typography
 *
 * Applies ADR-043: Vue Landing Components Architecture
 */

// Datos del responsable del tratamiento (centralizados en utils/constants/legal.js)
// Usamos RESPONSABLE_TRATAMIENTO directamente en el template para consistencia con TermsConditions.vue

// Page metadata
const pageTitle = ref('Política de Privacidad')
const pageDescription = ref('Información sobre cómo tratamos y protegemos sus datos personales conforme al RGPD y la LOPDGDD')
const lastUpdated = ref('22 de enero de 2026')
const policyVersion = ref('4.0')

// Analytics tracking for legal page views
if (typeof window !== 'undefined' && window.gtag) {
  window.gtag('event', 'legal_page_view', {
    event_category: 'legal_compliance',
    event_label: 'privacy_policy',
    value: 1
  })
}
</script>
